import React, { ReactNode, useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Box, Button, Skeleton } from '@mui/material'
import Popover from '@mui/material/Popover'
import * as datefns from 'date-fns'
import { CompanyShare } from '../../../types/CompanyEntity'
import { FactsTableRowFlexibleColumns } from '../../components/FactsTable'
import { CalculateVwapForm } from './CalculateVwapForm'
import { TradesStat } from '../../../types/Instrument'
import { formatMoney } from '../../../packages/utils/numberFormatters'
import { InstrumentApi } from '../../../api/InstrumentApi'
import DateRange from '../../../components/DateRange'

const VwapColumn = ({
    averageTurnover,
    currency,
    errorMsg,
    instrumentId,
    isLoading,
    numberOfDays,
    onChange,
    vwap,
}: {
    averageTurnover: number | null
    currency: string | null
    errorMsg: ReactNode
    instrumentId: string
    isLoading: boolean
    numberOfDays: number
    onChange: (instrumentId: string, endDate: Date, numberOfDays: number) => void
    vwap: number | null
}) => {
    const { i18n, t } = useTranslation()
    const locale = i18n.language
    const popoverId = useId()
    const [endDate, setEndDate] = useState<Date>(datefns.subDays(new Date(), 1))
    const [anchorEl, setAnchorEl] = useState<Element | null>(null)

    useEffect(() => onChange(instrumentId, endDate, numberOfDays), [])

    const handleChange = (iid: string, date: Date, days: number) => {
        setEndDate(date)
        onChange(iid, date, days)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleClickChange = (event: React.MouseEvent) => {
        setAnchorEl(event.currentTarget)
    }

    return (
        <React.Fragment key={`vwap-${instrumentId}`}>
            {isLoading && (
                <>
                    <Skeleton variant='text' width={40} />
                    <Skeleton variant='text' width={80} />
                </>
            )}
            {!isLoading && (
                <>
                    <div>
                        {formatMoney(vwap, { locale, minDecimals: 2, maxDecimals: 5 }) || '-'} <br />
                        {formatMoney(averageTurnover, { locale, decimals: 0, currencySymbol: currency }) || '-'}
                    </div>
                </>
            )}
            <Button size='small' onClick={handleClickChange} sx={{ paddingX: 0, justifyContent: 'flex-start' }}>
                {t('site.actions.edit')}
            </Button>
            <Popover
                id={popoverId}
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{ vertical: -20, horizontal: 'center' }}
            >
                <Box sx={{ padding: '20px 30px' }}>
                    <CalculateVwapForm
                        endDate={endDate}
                        numberOfDays={numberOfDays}
                        instrumentId={instrumentId}
                        onChange={handleChange}
                    />
                    {errorMsg}
                </Box>
            </Popover>
        </React.Fragment>
    )
}

export const VwapTableRow = ({ sharesRange }: { sharesRange: CompanyShare[] }) => {
    const { t } = useTranslation()
    const [tradesStat, setTradesStat] = useState<TradesStat | null>(null)
    const [numberOfDays, setNumberOfDays] = useState<number>(10)
    const [errorMsg, setErrorMsg] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    const handleFormChange = React.useCallback((instrumentId: string, endDate: Date, days: number) => {
        const instrumentApi = new InstrumentApi()

        void (async () => {
            try {
                setIsLoading(true)
                const tradeStats = await instrumentApi.getTradesStatsByEndingDate(instrumentId, endDate, days)

                setTradesStat(tradeStats)
                setNumberOfDays(days)
                setErrorMsg(null)
                setIsLoading(false)
            } catch (err: unknown) {
                console.log(err)
                setErrorMsg((err as { message?: string }).message || null)
            }
        })()
    }, [])

    return (
        <>
            <FactsTableRowFlexibleColumns
                key='vwap-table-row'
                firstColumnValue={
                    <div>
                        {t('pages.companies.vwap.vwapDays', { n: numberOfDays })} <br />
                        {t('pages.companies.vwap.turnoverPerDay')} <br />
                        <DateRange endDate={tradesStat?.endDate} startDate={tradesStat?.startDate} />
                    </div>
                }
                columns={sharesRange}
                getColumnValue={(share: CompanyShare) => {
                    return (
                        <>
                            <VwapColumn
                                averageTurnover={tradesStat?.avgTurnover || null}
                                currency={share.currency}
                                errorMsg={
                                    errorMsg && (
                                        <Alert severity='error' color='error'>
                                            {errorMsg}
                                        </Alert>
                                    )
                                }
                                numberOfDays={numberOfDays}
                                onChange={handleFormChange}
                                instrumentId={share.instrumentId}
                                isLoading={isLoading}
                                vwap={tradesStat?.vwap || null}
                            />
                        </>
                    )
                }}
                lastColumnValue={null}
                valign='top'
            />
        </>
    )
}
